import { alpha, createTheme, lighten } from '@mui/material';
import type { PaletteMode } from '@mui/material';

const lightColors = {
  canvas: '#F5F7FA',
  dashboard: '#F5F7FA',
  paper: '#FFFFFF',
  surfaceSubtle: '#F7FBFF',
  input: '#FFFFFF',
  line: '#E6EAEF',
  inputLine: '#CFE4F7',
  inputHover: '#8CC7F7',
  ink: '#172033',
  muted: '#667085',
} as const;

const darkColors = {
  canvas: '#0F172A',
  dashboard: '#111827',
  paper: '#182235',
  surfaceSubtle: '#1D293B',
  input: '#111B2D',
  line: '#334155',
  inputLine: '#3B4B62',
  inputHover: '#559FE8',
  ink: '#E7EEF8',
  muted: '#AAB7CA',
} as const;

export const layout = {
  toolbarHeight: 60,
  sidebarWidth: 240,
  sidebarCollapsedWidth: 72,
} as const;

declare module '@mui/material/styles' {
  interface TypeBackground {
    default: string;
    paper: string;
    dashboardMainContent: string;
  }

  interface Theme {
    layout: {
      toolbarHeight: number;
      sidebarWidth: number;
      sidebarCollapsedWidth: number;
    };
  }

  interface ThemeOptions {
    layout?: {
      toolbarHeight?: number;
      sidebarWidth?: number;
      sidebarCollapsedWidth?: number;
    };
  }
}

declare module '@mui/material/Card' {
  interface CardOwnProps {
    variant: 'error' | 'success' | 'default';
  }
}

export const createAppTheme = (mode: PaletteMode = 'light') => {
  const isDark = mode === 'dark';
  const colors = isDark ? darkColors : lightColors;
  const primary = '#0072E5';
  const error = isDark ? '#F97066' : '#B42318';
  const warning = isDark ? '#FDB022' : '#B54708';
  const success = isDark ? '#47CD89' : '#157347';

  return createTheme({
    layout,
    palette: {
      mode,
      primary: {
        main: primary,
        dark: '#0059B3',
        light: isDark ? '#173B5E' : '#E6F3FC',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: isDark ? '#9AAAC0' : '#4B6580',
        dark: isDark ? '#718198' : '#344054',
        light: isDark ? '#28364A' : '#EEF5FB',
      },
      text: {
        primary: colors.ink,
        secondary: colors.muted,
      },
      background: {
        default: colors.canvas,
        paper: colors.paper,
        dashboardMainContent: colors.dashboard,
      },
      divider: colors.line,
      action: {
        active: colors.muted,
        hover: alpha(primary, isDark ? 0.12 : 0.06),
        selected: alpha(primary, isDark ? 0.18 : 0.1),
        disabled: alpha(colors.ink, 0.38),
        disabledBackground: alpha(colors.ink, isDark ? 0.1 : 0.06),
        focus: alpha(primary, 0.18),
      },
      error: {
        main: error,
      },
      warning: {
        main: warning,
      },
      success: {
        main: success,
      },
      info: {
        main: isDark ? '#53B1FD' : primary,
      },
    },
    shape: {
      borderRadius: 10,
    },
    typography: {
      fontFamily:
        'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      h1: {
        fontSize: '1.625rem',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.25,
      },
      h2: {
        fontSize: '1.25rem',
        fontWeight: 700,
        letterSpacing: '-0.015em',
        lineHeight: 1.3,
      },
      h3: {
        fontSize: '1.0625rem',
        fontWeight: 700,
        letterSpacing: '-0.01em',
        lineHeight: 1.35,
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 700,
        letterSpacing: '-0.01em',
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 650,
        lineHeight: 1.4,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      caption: {
        fontSize: '0.75rem',
        letterSpacing: '0.04em',
        fontWeight: 600,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: 0,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            boxSizing: 'border-box',
          },
          html: {
            backgroundColor: colors.canvas,
            colorScheme: mode,
            '--synapsys-divider': colors.line,
          },
          body: {
            backgroundColor: colors.canvas,
            color: colors.ink,
          },
          '#root': {
            minHeight: '100vh',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            fontWeight: 700,
            lineHeight: 1.25,
            '&:focus-visible': {
              outline: `3px solid ${alpha(primary, 0.28)}`,
              outlineOffset: 2,
            },
          },
          sizeMedium: {
            padding: '9px 14px',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          outlined: {
            borderColor: colors.line,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: colors.input,
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.inputHover,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: primary,
              borderWidth: 1,
              boxShadow: `0 0 0 3px ${alpha(primary, 0.18)}`,
            },
          },
          notchedOutline: {
            borderColor: colors.inputLine,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              color: colors.muted,
              opacity: 0.8,
            },
            '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus':
              {
                WebkitBoxShadow: `0 0 0 100px ${colors.input} inset`,
                WebkitTextFillColor: colors.ink,
                caretColor: colors.ink,
                transition: 'background-color 5000s ease-in-out 0s',
              },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 650,
          },
          sizeSmall: {
            height: 24,
            fontSize: '0.75rem',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: `1px solid ${colors.line}`,
            boxShadow: `0 20px 48px ${alpha('#020617', isDark ? 0.48 : 0.18)}`,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${colors.line}`,
            boxShadow: `0 12px 28px ${alpha('#020617', isDark ? 0.42 : 0.14)}`,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            border: `1px solid ${colors.line}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            border: `1px solid ${colors.line}`,
            backgroundImage: 'none',
          },
          option: {
            '&[aria-selected="true"]': {
              backgroundColor: alpha(primary, isDark ? 0.2 : 0.1),
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 6,
            backgroundColor: isDark ? '#26364C' : '#0059B3',
            color: '#FFFFFF',
            border: `1px solid ${
              isDark ? colors.line : alpha('#FFFFFF', 0.12)
            }`,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: layout.toolbarHeight,
            '@media (min-width:600px)': {
              minHeight: layout.toolbarHeight,
            },
          },
        },
      },
      MuiCard: {
        variants: [
          {
            props: { variant: 'error' },
            style: {
              backgroundColor: isDark
                ? alpha(error, 0.12)
                : lighten(error, 0.9),
              borderColor: alpha(error, isDark ? 0.45 : 0.22),
            },
          },
          {
            props: { variant: 'success' },
            style: {
              backgroundColor: isDark
                ? alpha(success, 0.12)
                : lighten(success, 0.9),
              borderColor: alpha(success, isDark ? 0.45 : 0.22),
            },
          },
          {
            props: { variant: 'default' },
            style: {
              backgroundColor: colors.surfaceSubtle,
            },
          },
        ],
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: colors.line,
          },
          head: {
            backgroundColor: colors.dashboard,
            color: colors.muted,
            fontWeight: 700,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&:focus-visible': {
              outline: `3px solid ${alpha(primary, 0.28)}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '&:focus-visible': {
              outline: `3px solid ${alpha(primary, 0.24)}`,
              outlineOffset: -2,
            },
          },
        },
      },
    },
  });
};

export default createAppTheme('light');
